import { proxy } from "valtio";

export interface TreeNode {
    id: number;
    name: string;
    children: TreeNode[];
    /** Campaign nodes only (MPN-1964): the scheduled flight and the client-facing status. */
    start_date?: string | null;
    end_date?: string | null;
    status?: CampaignStatus | null;
}

/** Matches engagement/services/client_campaign_status.py. */
export type CampaignStatus = "UPCOMING" | "LIVE" | "ENDED";

/**
 * What the filter bar can ask for. ALL is the unfiltered default, and UPCOMING is absent on
 * purpose: an upcoming campaign has no performance data, so the view could only ever be the
 * empty state (PO review). UPCOMING survives as a campaign *state* — the All view labels one
 * with its start date — and the server rejects it as a status, so the two lists cannot drift.
 */
export type StatusFilter = "ALL" | "LIVE" | "ENDED";

function syncToUrl() {
    const params = new URLSearchParams();
    if (clientDashboardEvents.orgIds.length > 0) {
        params.set("orgs", clientDashboardEvents.orgIds.join(","));
    }
    if (clientDashboardEvents.status !== "ALL") {
        params.set("status", clientDashboardEvents.status);
    }
    const qs = params.toString().replace(/%2C/g, ",");
    const url = window.location.pathname + (qs ? `?${qs}` : "");
    window.history.replaceState(null, "", url);
}

export const clientDashboardEvents = proxy({
    filterChangedTrigger: 0,
    orgIds: [] as number[],
    days: "30",
    status: "ALL" as StatusFilter,
    engagement: null as number | null,
    objective: null as number | null,
    launch: null as number | null,
    filterTree: [] as TreeNode[],

    initFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("status");
        if (status === "LIVE" || status === "ENDED") {
            clientDashboardEvents.status = status;
        }
        // Anything else, "UPCOMING" from a bookmark saved while that option existed included,
        // falls through to All rather than being sent on to an API that now rejects it.
        const multi = params.get("orgs");
        if (multi) {
            clientDashboardEvents.orgIds = multi
                .split(",")
                .map((s) => Number(s.trim()))
                .filter((n) => Number.isFinite(n) && n > 0);
            return;
        }
        // Legacy single-org param
        const legacy = params.get("org");
        if (legacy) {
            const id = Number(legacy);
            if (Number.isFinite(id) && id > 0) {
                clientDashboardEvents.orgIds = [id];
            }
        }
    },
    triggerFilterChanged() {
        syncToUrl();
        clientDashboardEvents.filterChangedTrigger += 1;
    },
    triggerOrgsChanged(orgIds: number[]) {
        clientDashboardEvents.orgIds = orgIds;
        clientDashboardEvents.engagement = null;
        clientDashboardEvents.objective = null;
        clientDashboardEvents.launch = null;
        clientDashboardEvents.triggerFilterChanged();
    },
    triggerStatusChanged(status: StatusFilter) {
        clientDashboardEvents.status = status;
        // Drop the drill-down, as an organization change does. Status and campaign are
        // independent narrowings on the server and they intersect: drill into a Live
        // campaign, switch to Upcoming, and every tile reads zero while the card above
        // still names that campaign and calls it Live — the unexplained screen of zeroes
        // this ticket exists to remove.
        clientDashboardEvents.engagement = null;
        clientDashboardEvents.objective = null;
        clientDashboardEvents.launch = null;
        clientDashboardEvents.triggerFilterChanged();
    },
    triggerDaysChanged(days: string) {
        clientDashboardEvents.days = days;
        clientDashboardEvents.triggerFilterChanged();
    },
    triggerEngagementChanged(id: number | null) {
        clientDashboardEvents.engagement = id;
        clientDashboardEvents.objective = null;
        clientDashboardEvents.launch = null;
        clientDashboardEvents.triggerFilterChanged();
    },
    triggerObjectiveChanged(id: number | null) {
        clientDashboardEvents.objective = id;
        clientDashboardEvents.launch = null;
        clientDashboardEvents.triggerFilterChanged();
    },
    triggerLaunchChanged(id: number | null) {
        clientDashboardEvents.launch = id;
        clientDashboardEvents.triggerFilterChanged();
    },
    setFilterTree(tree: TreeNode[]) {
        clientDashboardEvents.filterTree = tree;
    },
});
